import { Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import axios from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { INewEvent } from '../../../models/IEvent';
import { format } from 'util';
import DateHelperInstance from '../../../services/DateHelper';
import UserAdminServiceInstance from '../../../services/UserAdminService';
import OntimeAutocomplete from '../../common/OntimeAutocomplete';
import config from '../../../config';
import AuthContext from '../../../services/Auth/AuthContext';
import CircuitServiceInstance from '../../../services/CircuitService';

interface INewEventErrors {
    name?: string;
    eventDate?: string;
    circuitId?: string;
    organizerId?: string;
}

interface INewEventProps {
    open: boolean,
    onCompleteEdit: (evt: INewEvent) => Promise<void>;
    onCancelEdit: () => void;
}

const getDefaultNewEvent = (): INewEvent => ({
    name: '',
    eventDate: new Date(),
    circuitId: '',
    organizerId: ''
});

export default function NewEventDialog(props: INewEventProps) {
    const [newEvent, setNewEvent] = useState<INewEvent>(getDefaultNewEvent);
    const [errors, setErrors] = React.useState<INewEventErrors>();
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation("common");
    const authService = useContext(AuthContext);
    const [circuits, setCircuits] = React.useState<any[]>([]);

    useEffect(() => {
        if (props.open) {
            setNewEvent(getDefaultNewEvent());
            setErrors(undefined);
        }
    }, [props.open]);

    useEffect(() => {
        const source = axios.CancelToken.source();
        const fetchCircuits = async () => {
            const cs = await CircuitServiceInstance.getCircuits(source.token);
            const items = cs.map(c => {
                return {
                    id: c.circuitId,
                    label: c.name
                }
            });
            setCircuits(items);

        }
        fetchCircuits().catch(reason => {
            if (!axios.isCancel(reason)) {
                console.error(reason);
            }
        });
        return () => {
            source.cancel();
        };
    }, []);

    const handleCompleteEdit = async () => {
        const currentErrors = checkErrors(newEvent);
        if (currentErrors) {
            return;
        }
        setLoading(true);
        try {
            await props.onCompleteEdit(newEvent);
        } finally {
            setLoading(false);
        }
    }
    const handleClose = () => {
        props.onCancelEdit();
    }
    const checkErrors = (eventData: INewEvent): INewEventErrors | undefined => {
        const error: INewEventErrors = {};
        let anyError = false;
        if (eventData.name.trim().length === 0) {
            error.name = format(t("commonErrors.requiredField"), t("eventsetup.name"));
            anyError = true;
        }
        if (eventData.circuitId.length === 0) {
            error.circuitId = format(t("commonErrors.requiredField"), t("eventsetup.circuit"));
            anyError = true;
        }
        if (!DateHelperInstance.isValidDate(eventData.eventDate)) {
            error.eventDate = format(t("commonErrors.requiredField"), t("eventsetup.date"));
            anyError = true;
        } else {
            if (DateHelperInstance.isInThePast(eventData.eventDate)) {
                error.eventDate = t<string>("eventsetup.errorEventInThePast");
                anyError = true;
            }
        }

        const resultingErrors = anyError ? error : undefined;
        setErrors(resultingErrors);
        return resultingErrors;
    }

    return (
        <Dialog open={props.open}>
            <DialogTitle>{t("eventsetup.new")}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    label={t("eventsetup.name")}
                    value={newEvent.name}
                    error={Boolean(errors?.name)}
                    id="name"
                    type="text"
                    fullWidth
                    margin="dense"
                    helperText={errors?.name}
                    onChange={(e) => {
                        setNewEvent((previous) => {
                            const updatedEvent = { ...previous, name: e.target.value };
                            checkErrors(updatedEvent);
                            return updatedEvent;
                        });
                    }} />
                <Autocomplete
                    id="circuitId"
                    options={circuits}
                    renderInput={(params) => <TextField {...params} label={t("eventsetup.circuit")} margin="dense" />}
                    onChange={(event, value) => {
                        setNewEvent((previous) => {
                            const updatedEvent = { ...previous, circuitId: value?.id ?? '' };
                            checkErrors(updatedEvent);
                            return updatedEvent;
                        });
                    }} />
                {authService?.isInRole(config.managementGroups.administrator) === true &&
                    <OntimeAutocomplete
                        label={t("eventsetup.organizer")}
                        itemIdProperty="userId"
                        id="organizerId"
                        itemLabelProperty="name"
                        valueId={newEvent.organizerId}
                        search={UserAdminServiceInstance.searchOrganizers}
                        onChange={(e, value) => {
                            setNewEvent((previous) => {
                                const updatedEvent = { ...previous, organizerId: value?.userId ?? '' };
                                checkErrors(updatedEvent);
                                return updatedEvent;
                            });
                        }} />}
                <TextField
                    label={t("eventsetup.eventDate")}
                    value={DateHelperInstance.datePickString(newEvent.eventDate)}
                    error={Boolean(errors?.eventDate)}
                    helperText={errors?.eventDate}
                    id="eventDate"
                    type="date"
                    fullWidth
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                        setNewEvent((previous) => {
                            const updatedEvent = { ...previous, eventDate: new Date(e.target.value) };
                            checkErrors(updatedEvent);
                            return updatedEvent;
                        });
                    }} />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCompleteEdit} color="primary" disabled={loading || errors !== undefined}>
                    {loading ? <CircularProgress size={20} /> : t("commonActions.ok")}
                </Button>
                <Button onClick={handleClose} color="primary" disabled={loading}>
                    {t("commonActions.cancel")}
                </Button>
            </DialogActions>
        </Dialog>);
}